import { Form, Input, InputNumber, Modal, Select } from "antd";
import { useEffect } from "react";
import {
  ICreateStationeryProduct,
  IQueryStationeryProduct,
  IStationeryCategory,
  IStationeryProduct,
} from "../../../models/Hrm";
import {
  useCreateStationeryProduct,
  useGetCategories,
  useUpdateStationeryProduct,
} from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";
import React from "react";
import { debounce } from "lodash";

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  editingCatalog: IStationeryProduct | null;
}

export const ModalAddStationeryProduct = ({
  isOpen,
  onCancel,
  editingCatalog,
}: Props) => {
  const [form] = Form.useForm<ICreateStationeryProduct>();
  const createMutation = useCreateStationeryProduct();
  const updateMutation = useUpdateStationeryProduct();
  const [allCategories, setAllCategories] = React.useState<
    IStationeryCategory[]
  >([]);

  const [filters, setFilters] = React.useState<IQueryStationeryProduct>({
    limit: 10,
    page: 1,
  });
  const {
    data: categories,
    isLoading: categoriesLoading,
    isFetching,
  } = useGetCategories(filters);

  useEffect(() => {
    if (categories?.data) {
      if (filters.page === 1) {
        setAllCategories(categories.data);
      } else {
        setAllCategories((prev) => [
          ...prev,
          ...categories.data.filter((c) => !prev.some((p) => p.id === c.id)),
        ]);
      }
    }
  }, [categories, filters.page]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setFilters({
          page: 1,
          limit: 10,
          name: value || undefined,
        });
      }, 300),
    [],
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  useEffect(() => {
    if (isOpen) {
      if (editingCatalog) {
        form.setFieldsValue({
          ...editingCatalog,
          categoryId: editingCatalog.category?.id, // An toàn hơn khi category có thể null
        });
      } else {
        form.resetFields();
      }
    }
  }, [editingCatalog, form, isOpen]); // Việc reset state đã có destroyOnClose lo

  const handleFormSubmit = async (values: ICreateStationeryProduct) => {
    try {
      if (editingCatalog) {
        await updateMutation.mutateAsync({
          id: editingCatalog.id,
          payload: values,
        });
        notify.success("Cập nhật thành công!");
      } else {
        await createMutation.mutateAsync(values);
        notify.success("Tạo mới thành công!");
      }
      onCancel();
    } catch (error: any) {
      notify.error(error.message);
    }
  };

  return (
    <Modal
      title={editingCatalog ? "Chỉnh sửa vật phẩm" : "Thêm vật phẩm mới"}
      open={isOpen}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending || updateMutation.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Form.Item
          name="name"
          label="Tên vật phẩm"
          rules={[{ required: true, message: "Vui lòng nhập tên vật phẩm" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="specification"
          label="Quy cách"
          rules={[{ required: true, message: "Vui lòng nhập quy cách" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="label"
          label="Nhãn"
          rules={[{ required: true, message: "Vui lòng nhập nhãn" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="categoryId"
          label="Danh mục"
          rules={[{ required: true, message: "Vui lòng chọn danh mục" }]}
        >
          <Select
            placeholder="Chọn danh mục"
            showSearch
            optionFilterProp="children"
            filterOption={false}
            onSearch={handleSearch}
            loading={categoriesLoading}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                !isFetching &&
                target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 10
              ) {
                if (
                  categories?.pagination &&
                  (filters.page || 1) * (filters.limit || 10) <
                    categories.pagination.total
                ) {
                  setFilters((prev) => ({
                    ...prev,
                    page: (prev.page || 1) + 1,
                  }));
                }
              }
            }}
          >
            {allCategories.map((cate) => (
              <Select.Option key={cate.id} value={cate.id}>
                {cate.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="unit"
          label="Đơn vị"
          rules={[{ required: true, message: "Vui lòng nhập đơn vị" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="price"
          label="Giá (VNĐ)"
          rules={[{ required: true, message: "Vui lòng nhập giá" }]}
        >
          <InputNumber<number>
            style={{ width: "100%" }}
            min={0}
            addonAfter=".000 VND"
            formatter={(value) =>
              value ? Number(value).toLocaleString("vi-VN") : ""
            }
            parser={(value) => Number(value?.replace(/\./g, "") || 0)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
